import jsQR from "jsqr";

declare global {
    interface Navigator {
        standalone?: boolean;
    }
}

const Sync: any = (globalThis as any).TrackMyCourseSync;
const DB_NAME = "track-my-course-phone";
const DB_VERSION = 1;
const STORE_NAME = "state";
const FOREGROUND_REFRESH_INTERVAL_MS = 10_000;
const CONFIGURATION_PERSIST_INTERVAL_MS = 60_000;

type SyncConfiguration = {
    serviceUrl: string;
    syncId: string;
    rootSecret: string;
    revision?: number;
    pairedAt?: number;
    lastSyncedAt?: number;
};

type PendingNativeSession = {
    courseId: string;
    videoId: string;
    startingTimestamp: number;
    createdAt: number;
};

type CourseDocument = {
    schemaVersion: number;
    courses: Record<string, any>;
    tombstones: Record<string, number>;
    updatedAt: number;
};

type AutoSession = {
    document: CourseDocument;
    configuration: SyncConfiguration;
    courseId: string;
    videoId: string;
    lastPlayerTime: number | null;
    lastPersistedAt: number;
};

type SynchronizationRequest = {
    configuration: SyncConfiguration;
    reason: string;
};

const elements = {
    installCard: byId<HTMLElement>("install-card"),
    pairScreen: byId<HTMLElement>("pair-screen"),
    dashboard: byId<HTMLElement>("dashboard"),
    courseList: byId<HTMLElement>("course-list"),
    emptyLibrary: byId<HTMLElement>("empty-library"),
    addCourseButton: byId<HTMLButtonElement>("add-course-button"),
    syncChip: byId<HTMLElement>("sync-chip"),
    deskSummary: byId<HTMLElement>("desk-summary"),
    scannerDialog: byId<HTMLDialogElement>("scanner-dialog"),
    scannerVideo: byId<HTMLVideoElement>("scanner-video"),
    scannerCanvas: byId<HTMLCanvasElement>("scanner-canvas"),
    scannerError: byId<HTMLElement>("scanner-error"),
    typedDialog: byId<HTMLDialogElement>("typed-pairing-dialog"),
    typedForm: byId<HTMLFormElement>("typed-pairing-form"),
    typedServiceUrl: byId<HTMLInputElement>("typed-service-url"),
    typedCode: byId<HTMLInputElement>("typed-pairing-code"),
    typedError: byId<HTMLElement>("typed-pairing-error"),
    addCourseDialog: byId<HTMLDialogElement>("add-course-dialog"),
    addCourseForm: byId<HTMLFormElement>("add-course-form"),
    playlistInput: byId<HTMLInputElement>("playlist-input"),
    addCourseError: byId<HTMLElement>("add-course-error"),
    submitCourseButton: byId<HTMLButtonElement>("submit-course-button"),
    manageCategoriesButton: byId<HTMLButtonElement>("manage-categories-button"),
    categoryDialog: byId<HTMLDialogElement>("category-dialog"),
    categoryForm: byId<HTMLFormElement>("category-form"),
    categoryCourseName: byId<HTMLElement>("category-course-name"),
    categoryNameInput: byId<HTMLInputElement>("category-name-input"),
    categorySuggestions: byId<HTMLDataListElement>("category-suggestions"),
    categoryInputError: byId<HTMLElement>("category-input-error"),
    categoryManagerDialog: byId<HTMLDialogElement>("category-manager-dialog"),
    categoryManagerList: byId<HTMLElement>("category-manager-list"),
    categoryManagerEmpty: byId<HTMLElement>("category-manager-empty"),
    checkinDialog: byId<HTMLDialogElement>("checkin-dialog"),
    checkinForm: byId<HTMLFormElement>("checkin-form"),
    checkinTitle: byId<HTMLElement>("checkin-title"),
    checkinCopy: byId<HTMLElement>("checkin-copy"),
    checkinStartMinutes: byId<HTMLInputElement>("checkin-start-minutes"),
    checkinStartSeconds: byId<HTMLInputElement>("checkin-start-seconds"),
    checkinMinutes: byId<HTMLInputElement>("checkin-minutes"),
    checkinSeconds: byId<HTMLInputElement>("checkin-seconds"),
    checkinSlider: byId<HTMLInputElement>("checkin-slider"),
    checkinDuration: byId<HTMLElement>("checkin-duration"),
    checkinError: byId<HTMLElement>("checkin-error"),
    checkinSaveButton: byId<HTMLButtonElement>("checkin-save-button"),
    automaticDialog: byId<HTMLDialogElement>("automatic-dialog"),
    automaticTitle: byId<HTMLElement>("automatic-title"),
    automaticFrame: byId<HTMLIFrameElement>("automatic-player-frame"),
    unpairDeviceButton: byId<HTMLButtonElement>("unpair-device-button"),
    toast: byId<HTMLElement>("toast"),
};

let cameraStream: MediaStream | null = null;
let scannerFrame = 0;
let currentPending: PendingNativeSession | null = null;
let checkinMode: "native" | "correction" | null = null;
let editingCategoryCourseId: string | null = null;
let automaticSession: AutoSession | null = null;
let activeConfiguration: SyncConfiguration | null = null;
let foregroundRefreshPromise: Promise<void> | null = null;
let synchronizationPromise: Promise<void> | null = null;
let queuedSynchronizationRequest: SynchronizationRequest | null = null;
let playlistOrderMutationQueue: Promise<void> = Promise.resolve();
let courseDocumentWriteQueue: Promise<void> = Promise.resolve();
let playlistOrderMutationGeneration = 0;
let toastTimer = 0;

void initialize();

async function initialize(): Promise<void> {
    bindControls();
    if ("serviceWorker" in navigator) void navigator.serviceWorker.register("/sw.js");

    const standalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        navigator.standalone === true ||
        (["localhost", "127.0.0.1"].includes(window.location.hostname) &&
            new URLSearchParams(window.location.search).get("standalone") === "1");
    if (!standalone) {
        elements.installCard.hidden = false;
        return;
    }

    const localPreview = ["localhost", "127.0.0.1"].includes(window.location.hostname);
    if (localPreview && new URLSearchParams(window.location.search).get("demo") === "1") {
        const configuration: SyncConfiguration = {
            serviceUrl: window.location.origin,
            syncId: "local-preview",
            rootSecret: "local-preview",
        };
        const document = makePreviewDocument();
        await setRecord("configuration", configuration);
        await setRecord("document", document);
        await showDashboard(configuration);
        renderDashboard(document, configuration);
        return;
    }

    const configuration = await getRecord<SyncConfiguration>("configuration");
    if (!configuration) {
        elements.pairScreen.hidden = false;
        return;
    }

    await showDashboard(configuration);
    await maybePromptForCheckin();
    void synchronize(configuration, "app-opened");
}

function bindControls(): void {
    byId<HTMLButtonElement>("scan-pairing-button").addEventListener("click", startScanner);
    byId<HTMLButtonElement>("show-typed-pairing-button").addEventListener("click", () =>
        elements.typedDialog.showModal()
    );
    elements.typedForm.addEventListener("submit", handleTypedPairing);
    elements.addCourseButton.addEventListener("click", openAddCourse);
    document
        .querySelectorAll<HTMLElement>("[data-add-course]")
        .forEach((button) => button.addEventListener("click", openAddCourse));
    elements.addCourseForm.addEventListener("submit", addCourse);
    elements.courseList.addEventListener("click", handleCourseAction);
    elements.manageCategoriesButton.addEventListener("click", () => void openCategoryManager());
    elements.categoryForm.addEventListener("submit", saveCourseCategory);
    elements.categoryManagerList.addEventListener("click", handleCategoryManagerAction);
    elements.categoryDialog.addEventListener("close", () => {
        editingCategoryCourseId = null;
        elements.categoryForm.reset();
        elements.categoryInputError.textContent = "";
    });
    elements.checkinForm.addEventListener("submit", (event) => event.preventDefault());
    elements.checkinSaveButton.addEventListener("click", saveCheckin);
    elements.checkinSlider.addEventListener("input", () => {
        writeCheckinTimeFields(
            Number(elements.checkinSlider.value),
            elements.checkinMinutes,
            elements.checkinSeconds
        );
        elements.checkinError.textContent = "";
    });
    bindCheckinTimeFields(
        elements.checkinStartMinutes,
        elements.checkinStartSeconds,
        elements.checkinMinutes,
        false
    );
    bindCheckinTimeFields(elements.checkinMinutes, elements.checkinSeconds, null, true);
    byId<HTMLButtonElement>("mark-complete-button").addEventListener("click", markCheckinComplete);
    byId<HTMLButtonElement>("no-progress-button").addEventListener("click", discardCheckin);

    document.querySelectorAll<HTMLButtonElement>("[data-close-dialog]").forEach((button) => {
        button.addEventListener("click", () =>
            byId<HTMLDialogElement>(button.dataset.closeDialog || "").close()
        );
    });
    elements.scannerDialog.addEventListener("close", stopScanner);
    elements.automaticDialog.addEventListener("close", stopAutomaticTracking);
    elements.unpairDeviceButton.addEventListener("click", unpairThisDevice);
    window.addEventListener("message", handleAutomaticPlayerMessage);

    window.addEventListener("online", () => void refreshVisibleApp("connection-restored"));
    window.addEventListener("pageshow", () => {
        void refreshVisibleApp("page-shown");
    });
    window.addEventListener("focus", () => {
        void refreshVisibleApp("window-focused");
    });
    document.addEventListener("visibilitychange", () => {
        if (document.hidden) {
            if (automaticSession)
                elements.automaticFrame.contentWindow?.postMessage(
                    { type: "course-desk-player-pause" },
                    "*"
                );
            if (automaticSession) void persistAutomaticSession(true);
            return;
        }
        void refreshVisibleApp("app-returned");
    });
    window.setInterval(
        () => void refreshVisibleApp("foreground-poll"),
        FOREGROUND_REFRESH_INTERVAL_MS
    );
}

function bindCheckinTimeFields(
    minutesInput: HTMLInputElement,
    secondsInput: HTMLInputElement,
    nextInput: HTMLInputElement | null,
    updatesSlider: boolean
): void {
    const inputs = [minutesInput, secondsInput];
    const updateSlider = (): void => {
        elements.checkinError.textContent = "";
        if (!updatesSlider) return;
        const parsed = parseCheckinTimeFields(minutesInput, secondsInput);
        const maximum = Number(elements.checkinSlider.max);
        if (parsed !== null && parsed <= maximum) elements.checkinSlider.value = String(parsed);
    };
    const normalizeFields = (): void => {
        const parsed = parseCheckinTimeFields(minutesInput, secondsInput);
        const maximum = Number(elements.checkinSlider.max);
        if (parsed === null || parsed > maximum) return;
        writeCheckinTimeFields(parsed, minutesInput, secondsInput);
        if (updatesSlider) elements.checkinSlider.value = String(parsed);
    };

    for (const input of inputs) {
        input.addEventListener("input", updateSlider);
        input.addEventListener("focus", () => {
            window.requestAnimationFrame(() => input.select());
        });
        input.addEventListener("change", normalizeFields);
        input.addEventListener("keydown", (event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            normalizeFields();
            if (input === minutesInput) secondsInput.focus();
            else if (nextInput) nextInput.focus();
            else input.blur();
        });
    }
}

function refreshVisibleApp(reason: string): Promise<void> {
    if (document.hidden || elements.dashboard.hidden) return Promise.resolve();
    if (foregroundRefreshPromise) return foregroundRefreshPromise;
    const refresh = (async () => {
        await maybePromptForCheckin();
        const configuration = await getRecord<SyncConfiguration>("configuration");
        if (configuration) await synchronize(configuration, reason);
    })().finally(() => {
        if (foregroundRefreshPromise === refresh) foregroundRefreshPromise = null;
    });
    foregroundRefreshPromise = refresh;
    return refresh;
}

async function showDashboard(configuration: SyncConfiguration): Promise<void> {
    activeConfiguration = configuration;
    elements.installCard.hidden = true;
    elements.pairScreen.hidden = true;
    elements.dashboard.hidden = false;
    elements.addCourseButton.hidden = false;
    elements.manageCategoriesButton.hidden = false;
    elements.unpairDeviceButton.hidden = false;
    elements.syncChip.hidden = false;
    updateSyncChip(navigator.onLine ? "ready" : "offline");
    const document = await getLocalDocument();
    renderDashboard(document, configuration);
}

function renderDashboard(document: CourseDocument, configuration: SyncConfiguration): void {
    const entries = Object.entries(document.courses).sort(
        ([, left], [, right]) =>
            Number(right.lastInteractedAt || 0) - Number(left.lastInteractedAt || 0)
    );
    elements.courseList.replaceChildren(
        ...getCategoryGroups(entries).map((group, index) =>
            createCategorySection(group, index, configuration)
        )
    );
    elements.emptyLibrary.hidden = entries.length > 0;

    let watched = 0;
    let total = 0;
    let complete = 0;
    let lessons = 0;
    for (const [, course] of entries) {
        watched += Sync.durationToSeconds(course.watchedDuration);
        total += Sync.durationToSeconds(course.totalDuration);
        const statuses = Object.values(course.videoWatchStatus || {});
        complete += statuses.filter(Boolean).length;
        lessons += statuses.length;
    }
    const progress = total ? Math.round((watched / total) * 1000) / 10 : 0;
    elements.deskSummary.textContent = `${progress}% across ${entries.length} course${entries.length === 1 ? "" : "s"} · ${complete}/${lessons} lessons complete`;
}

function createCategorySection(
    group: { name: string; courses: Array<[string, any]> },
    index: number,
    configuration: SyncConfiguration
): HTMLElement {
    const section = document.createElement("section");
    section.className = "category-section";
    const heading = document.createElement("div");
    heading.className = "category-section-heading";
    const order = document.createElement("span");
    order.className = "category-section-order";
    order.textContent = String(index + 1).padStart(2, "0");
    const title = document.createElement("h2");
    title.textContent = group.name || "Uncategorized";
    const count = document.createElement("span");
    count.className = "category-section-count";
    count.textContent = `${group.courses.length} course${group.courses.length === 1 ? "" : "s"}`;
    heading.appendChild(order);
    heading.appendChild(title);
    heading.appendChild(count);
    const shelf = document.createElement("div");
    shelf.className = "category-course-list";
    shelf.replaceChildren(
        ...group.courses.map(([courseId, course]) =>
            createCourseCard(courseId, course, configuration)
        )
    );
    section.appendChild(heading);
    section.appendChild(shelf);
    return section;
}

function createCourseCard(
    courseId: string,
    course: any,
    configuration: SyncConfiguration
): HTMLElement {
    const recalculated = Sync.recalculateCourse(course);
    const watched = Sync.durationToSeconds(recalculated.watchedDuration);
    const total = Sync.durationToSeconds(recalculated.totalDuration);
    const progress = total ? Math.min(100, Math.round((watched / total) * 1000) / 10) : 0;
    const videoIds: string[] = Sync.getOrderedPlaylistVideoIds(recalculated);
    const complete = videoIds.filter((videoId) => recalculated.videoWatchStatus[videoId]).length;
    const resumeVideoId = Sync.getResumeVideoId(recalculated) || "";
    const resumeAt = Number(recalculated.videoPlaybackPositions?.[resumeVideoId]) || 0;
    const playlistOrder =
        recalculated.playlistOrder === "reversed" || recalculated.playlistOrder === "custom"
            ? recalculated.playlistOrder
            : "default";
    const courseName = recalculated.courseName || "Untitled course";

    const card = document.createElement("article");
    card.className = "course-card";
    card.dataset.courseCard = courseId;
    card.innerHTML = `
        <div class="course-head">
            <div class="course-cover"><img alt="" /><span class="course-percent"></span></div>
            <div class="course-copy">
                <p class="eyebrow">${complete === videoIds.length && videoIds.length ? "Completed" : "In progress"}</p>
                <h2 dir="auto"></h2>
                <p class="course-meta"></p>
                <div class="progress-track" role="progressbar" aria-valuemin="0" aria-valuemax="100"><span></span></div>
                <div class="course-actions">
                    <a class="primary-button" href="${escapeAttribute(buildYouTubeWatchUrl(courseId, resumeVideoId || "", resumeAt))}" rel="external" data-action="native" data-course="${escapeAttribute(courseId)}" data-video="${escapeAttribute(resumeVideoId || "")}" data-starting-timestamp="${Math.max(0, Math.floor(resumeAt))}">Resume in YouTube Premium</a>
                    <a class="playlist-button" href="${escapeAttribute(buildYouTubePlaylistUrl(courseId))}" rel="external">Prepare course offline</a>
                    <button class="playlist-button category-course-button" type="button" data-action="category" data-course="${escapeAttribute(courseId)}">${recalculated.categoryName ? `Category: ${escapeAttribute(recalculated.categoryName)}` : "Add category"}</button>
                    <div class="playlist-order-control">
                        <span class="playlist-order-label">Lesson order</span>
                        <div class="playlist-order-options" role="group" aria-label="Lesson order for ${escapeAttribute(courseName)}">
                            <button type="button" data-action="order-mode" data-order-mode="default" data-course="${escapeAttribute(courseId)}" aria-pressed="${playlistOrder === "default"}">Creator</button>
                            <button type="button" data-action="order-mode" data-order-mode="reversed" data-course="${escapeAttribute(courseId)}" aria-pressed="${playlistOrder === "reversed"}">Reverse</button>
                            <button type="button" data-action="order-mode" data-order-mode="custom" data-course="${escapeAttribute(courseId)}" aria-pressed="${playlistOrder === "custom"}">Custom</button>
                        </div>
                    </div>
                    ${
                        playlistOrder === "default"
                            ? ""
                            : '<p class="playlist-order-native-note">YouTube’s Next follows the original playlist. Return to Course Desk for your next lesson in this order.</p>'
                    }
                </div>
            </div>
        </div>
        <details class="lesson-disclosure"><summary>${videoIds.length} lessons · show details</summary><div class="lesson-list"></div></details>`;
    const image = card.querySelector<HTMLImageElement>("img")!;
    image.src =
        recalculated.courseImgSrc ||
        recalculated.videoMetadata?.[videoIds[0]]?.thumbnailUrl ||
        "/icon.svg";
    card.querySelector<HTMLElement>(".course-percent")!.textContent = `${progress}%`;
    card.querySelector<HTMLElement>("h2")!.textContent = courseName;
    card.querySelector<HTMLElement>(".course-meta")!.textContent =
        `${formatDuration(watched)} uniquely watched · ${formatDuration(Math.max(0, total - watched))} left${resumeAt ? ` · saved ${formatClock(resumeAt)}` : ""}`;
    const track = card.querySelector<HTMLElement>(".progress-track")!;
    track.setAttribute("aria-valuenow", String(progress));
    track.setAttribute("aria-label", `${progress}% complete`);
    track.querySelector<HTMLElement>("span")!.style.width = `${progress}%`;
    const lessonList = card.querySelector<HTMLElement>(".lesson-list")!;
    lessonList.replaceChildren(
        ...videoIds.map((videoId, index) =>
            createLessonRow(courseId, recalculated, videoId, index, videoIds.length, configuration)
        )
    );
    return card;
}

function createLessonRow(
    courseId: string,
    course: any,
    videoId: string,
    index: number,
    lessonCount: number,
    _configuration: SyncConfiguration
): HTMLElement {
    const metadata = course.videoMetadata?.[videoId] || {};
    const duration = Number(course.videoDurations?.[videoId]) || 0;
    const watched = Number(course.videoWatchedSeconds?.[videoId]) || 0;
    const position = Number(course.videoPlaybackPositions?.[videoId]) || 0;
    const progress = duration ? Math.min(100, Math.round((watched / duration) * 100)) : 0;
    const isCustomOrder = course.playlistOrder === "custom";
    const row = document.createElement("div");
    row.className = "lesson-row";
    row.innerHTML = `
        <span class="lesson-index">${index + 1}</span>
        <div class="lesson-main">
            <h3 dir="auto"></h3><p class="lesson-meta"></p>
            <div class="lesson-track"><span></span></div>
            <div class="lesson-actions">
                <a class="lesson-button is-primary" href="${escapeAttribute(buildYouTubeWatchUrl(courseId, videoId, position))}" rel="external" data-action="native" data-course="${escapeAttribute(courseId)}" data-video="${escapeAttribute(videoId)}" data-starting-timestamp="${Math.max(0, Math.floor(position))}">${position ? `Resume ${formatClock(position)}` : "Play in Premium"}</a>
                <a class="lesson-button is-download" href="${escapeAttribute(buildYouTubeWatchUrl(courseId, videoId, 0))}" rel="external">Open in YouTube to download</a>
                <button class="lesson-button" type="button" data-action="automatic" data-course="${escapeAttribute(courseId)}" data-video="${escapeAttribute(videoId)}">Track automatically</button>
                <button class="lesson-button" type="button" data-action="correct" data-course="${escapeAttribute(courseId)}" data-video="${escapeAttribute(videoId)}">Correct watched time</button>
            </div>
            ${
                isCustomOrder
                    ? `<div class="lesson-reorder-actions" role="group" aria-label="Change position of lesson ${index + 1} in ${escapeAttribute(course.courseName || "this course")}">
                <span>Position</span>
                <button type="button" data-action="move-lesson" data-direction="up" data-course="${escapeAttribute(courseId)}" data-video="${escapeAttribute(videoId)}" aria-label="Move lesson ${index + 1} up" ${index === 0 ? "disabled" : ""}><span aria-hidden="true">↑</span> Move up</button>
                <button type="button" data-action="move-lesson" data-direction="down" data-course="${escapeAttribute(courseId)}" data-video="${escapeAttribute(videoId)}" aria-label="Move lesson ${index + 1} down" ${index === lessonCount - 1 ? "disabled" : ""}><span aria-hidden="true">↓</span> Move down</button>
            </div>`
                    : ""
            }
        </div>`;
    row.querySelector<HTMLElement>("h3")!.textContent = metadata.title || `Lesson ${index + 1}`;
    row.querySelector<HTMLElement>(".lesson-meta")!.textContent =
        `${formatClock(duration)} · ${progress}% uniquely watched${course.videoWatchStatus?.[videoId] ? " · complete" : ""}`;
    row.querySelector<HTMLElement>(".lesson-track span")!.style.width = `${progress}%`;
    return row;
}

function handleCourseAction(event: Event): void {
    const control = (event.target as HTMLElement).closest<HTMLElement>("[data-action]");
    if (!control) return;
    const courseId = control.dataset.course || "";
    const videoId = control.dataset.video || "";
    if (control.dataset.action === "native") {
        rememberNativeSession(control);
        return;
    }
    if (control.dataset.action === "automatic") {
        event.preventDefault();
        void startAutomaticTracking(courseId, videoId);
        return;
    }
    if (control.dataset.action === "correct") {
        event.preventDefault();
        void openProgressCorrection(courseId, videoId);
        return;
    }
    if (control.dataset.action === "category") {
        event.preventDefault();
        void openCourseCategoryDialog(courseId);
        return;
    }
    if (control.dataset.action === "order-mode") {
        event.preventDefault();
        const playlistOrder = control.dataset.orderMode || "";
        queuePlaylistOrderMutation(() => setCoursePlaylistOrder(courseId, playlistOrder));
        return;
    }
    if (control.dataset.action === "move-lesson") {
        event.preventDefault();
        const direction = control.dataset.direction === "up" ? "up" : "down";
        queuePlaylistOrderMutation(() => moveCourseLesson(courseId, videoId, direction));
    }
}

function queuePlaylistOrderMutation(mutation: () => Promise<void>): void {
    playlistOrderMutationQueue = playlistOrderMutationQueue.then(mutation, mutation);
}

async function setCoursePlaylistOrder(courseId: string, requestedOrder: string): Promise<void> {
    if (!courseId) return;
    if (!["default", "reversed", "custom"].includes(requestedOrder)) return;
    const courseDocument = await getLocalDocument();
    const course = courseDocument.courses[courseId];
    if (!course) return;

    const currentOrder =
        course.playlistOrder === "reversed" || course.playlistOrder === "custom"
            ? course.playlistOrder
            : "default";
    if (currentOrder === requestedOrder) return;

    const now = nextPlaylistOrderTimestamp(course);
    const changes: Record<string, unknown> = {
        playlistOrder: requestedOrder,
        playlistOrderUpdatedAt: now,
        courseUpdatedAt: now,
    };
    if (requestedOrder === "custom") {
        const savedCustomOrder = Sync.normalizePlaylistCustomOrder(course.playlistCustomOrder);
        const hasSavedCustomOrder =
            savedCustomOrder.length > 0 && Number(course.playlistCustomOrderUpdatedAt) > 0;
        if (!hasSavedCustomOrder) {
            changes.playlistCustomOrder = Sync.getOrderedPlaylistVideoIds(
                Sync.recalculateCourse(course)
            );
            changes.playlistCustomOrderUpdatedAt = now;
        }
    }
    courseDocument.courses[courseId] = Sync.recalculateCourse({
        ...course,
        ...changes,
    });
    courseDocument.updatedAt = now;
    renderCourseOrderUpdate(courseDocument, courseId, { orderMode: requestedOrder });
    const saved = await persistPlaylistOrderChange(courseDocument, "playlist-order-changed");
    if (!saved) return;
    const label = { default: "creator", reversed: "reverse", custom: "custom" }[requestedOrder];
    showToast(`Lessons shown in ${label} order`);
}

async function moveCourseLesson(
    courseId: string,
    videoId: string,
    direction: "up" | "down"
): Promise<void> {
    if (!courseId || !videoId) return;
    const courseDocument = await getLocalDocument();
    const course = courseDocument.courses[courseId];
    if (!course || course.playlistOrder !== "custom") return;

    const customOrder: string[] = Sync.getOrderedPlaylistVideoIds(Sync.recalculateCourse(course));
    const currentIndex = customOrder.indexOf(videoId);
    const targetIndex = currentIndex + (direction === "up" ? -1 : 1);
    if (currentIndex < 0 || targetIndex < 0 || targetIndex >= customOrder.length) return;
    [customOrder[currentIndex], customOrder[targetIndex]] = [
        customOrder[targetIndex],
        customOrder[currentIndex],
    ];

    const now = nextPlaylistOrderTimestamp(course);
    courseDocument.courses[courseId] = Sync.recalculateCourse({
        ...course,
        playlistOrder: "custom",
        playlistOrderUpdatedAt: now,
        playlistCustomOrder: customOrder,
        playlistCustomOrderUpdatedAt: now,
        courseUpdatedAt: now,
    });
    courseDocument.updatedAt = now;
    renderCourseOrderUpdate(courseDocument, courseId, { videoId, direction });
    const saved = await persistPlaylistOrderChange(courseDocument, "playlist-custom-order-changed");
    if (!saved) return;
    showToast(`Lesson moved to position ${targetIndex + 1}`);
}

function nextPlaylistOrderTimestamp(course: any): number {
    return Math.max(
        Date.now(),
        (Number(course?.playlistOrderUpdatedAt) || 0) + 1,
        (Number(course?.playlistCustomOrderUpdatedAt) || 0) + 1,
        (Number(course?.courseUpdatedAt) || 0) + 1
    );
}

function renderCourseOrderUpdate(
    courseDocument: CourseDocument,
    courseId: string,
    focus: { orderMode?: string; videoId?: string; direction?: "up" | "down" }
): void {
    if (!activeConfiguration) return;
    const previousCard = [
        ...elements.courseList.querySelectorAll<HTMLElement>(".course-card"),
    ].find((card) => card.dataset.courseCard === courseId);
    if (!previousCard) {
        renderDashboard(courseDocument, activeConfiguration);
        return;
    }
    const wasOpen = previousCard.querySelector<HTMLDetailsElement>(".lesson-disclosure")?.open;
    const nextCard = createCourseCard(
        courseId,
        courseDocument.courses[courseId],
        activeConfiguration
    );
    const nextDetails = nextCard.querySelector<HTMLDetailsElement>(".lesson-disclosure");
    if (nextDetails) nextDetails.open = Boolean(wasOpen);
    previousCard.replaceWith(nextCard);

    requestAnimationFrame(() => {
        let target: HTMLElement | undefined;
        if (focus.orderMode) {
            target = [...nextCard.querySelectorAll<HTMLElement>('[data-action="order-mode"]')].find(
                (control) => control.dataset.orderMode === focus.orderMode
            );
        } else if (focus.videoId) {
            const lessonMoveControls = [
                ...nextCard.querySelectorAll<HTMLElement>('[data-action="move-lesson"]'),
            ].filter((control) => control.dataset.video === focus.videoId);
            target = lessonMoveControls.find(
                (control) =>
                    control.dataset.direction === focus.direction &&
                    !(control as HTMLButtonElement).disabled
            );
            target ||= lessonMoveControls.find(
                (control) => !(control as HTMLButtonElement).disabled
            );
        }
        target?.focus({ preventScroll: true });
    });
}

async function persistPlaylistOrderChange(
    courseDocument: CourseDocument,
    reason: string
): Promise<boolean> {
    try {
        await persistCourseDocument(courseDocument, reason, false, true);
        return true;
    } catch (error) {
        console.warn("Could not save lesson order", error);
        const savedDocument = await getLocalDocument();
        if (activeConfiguration) renderDashboard(savedDocument, activeConfiguration);
        showToast("Could not save lesson order. Try again.");
        return false;
    }
}

function normalizeCategoryName(value: unknown): string {
    return String(value || "")
        .trim()
        .replace(/\s+/g, " ")
        .slice(0, 60);
}

function getCategoryDefinitions(
    courses: any[]
): Array<{ name: string; order: number; count: number }> {
    const categories = new Map<string, { name: string; order: number; count: number }>();
    for (const course of courses) {
        const name = normalizeCategoryName(course.categoryName);
        if (!name) continue;
        const key = name.toLocaleLowerCase();
        const order = Math.max(0, Number(course.categoryOrder) || 0);
        const current = categories.get(key);
        if (!current) categories.set(key, { name, order, count: 1 });
        else {
            current.order = Math.min(current.order, order);
            current.count++;
        }
    }
    return [...categories.values()].sort(
        (left, right) => left.order - right.order || left.name.localeCompare(right.name)
    );
}

function getCategoryGroups(
    entries: Array<[string, any]>
): Array<{ name: string; courses: Array<[string, any]> }> {
    const definitions = getCategoryDefinitions(entries.map(([, course]) => course));
    const groups = definitions.map((category) => ({
        name: category.name,
        courses: [] as Array<[string, any]>,
    }));
    const byName = new Map(groups.map((group) => [group.name.toLocaleLowerCase(), group]));
    const uncategorized = { name: "", courses: [] as Array<[string, any]> };
    for (const entry of entries) {
        const name = normalizeCategoryName(entry[1].categoryName);
        const group = name ? byName.get(name.toLocaleLowerCase()) : uncategorized;
        (group || uncategorized).courses.push(entry);
    }
    if (uncategorized.courses.length) groups.push(uncategorized);
    return groups;
}

async function openCourseCategoryDialog(courseId: string): Promise<void> {
    const courseDocument = await getLocalDocument();
    const course = courseDocument.courses[courseId];
    if (!course) return;
    editingCategoryCourseId = courseId;
    elements.categoryCourseName.textContent = course.courseName || "Untitled course";
    elements.categoryNameInput.value = course.categoryName || "";
    elements.categorySuggestions.replaceChildren(
        ...getCategoryDefinitions(Object.values(courseDocument.courses)).map((category) => {
            const option = document.createElement("option");
            option.value = category.name;
            return option;
        })
    );
    elements.categoryInputError.textContent = "";
    elements.categoryDialog.showModal();
    requestAnimationFrame(() => elements.categoryNameInput.focus());
}

async function saveCourseCategory(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (!editingCategoryCourseId) return;
    const courseDocument = await getLocalDocument();
    const course = courseDocument.courses[editingCategoryCourseId];
    if (!course) return;
    const requestedName = normalizeCategoryName(elements.categoryNameInput.value);
    const categories = getCategoryDefinitions(Object.values(courseDocument.courses));
    const existing = categories.find(
        (category) => category.name.toLocaleLowerCase() === requestedName.toLocaleLowerCase()
    );
    const categoryName = existing?.name || requestedName;
    const categoryOrder = categoryName
        ? (existing?.order ?? Math.max(-1, ...categories.map((category) => category.order)) + 1)
        : 0;
    const now = Date.now();
    course.categoryName = categoryName;
    course.categoryOrder = categoryOrder;
    course.categoryUpdatedAt = now;
    course.courseUpdatedAt = now;
    courseDocument.courses[editingCategoryCourseId] = Sync.recalculateCourse(course);
    courseDocument.updatedAt = now;
    elements.categoryDialog.close();
    await persistCourseDocument(courseDocument, "category-assigned");
    showToast(categoryName ? `Filed under ${categoryName}` : "Moved to Uncategorized");
}

async function openCategoryManager(): Promise<void> {
    const courseDocument = await getLocalDocument();
    renderCategoryManager(courseDocument);
    elements.categoryManagerDialog.showModal();
}

function renderCategoryManager(courseDocument: CourseDocument): void {
    const categories = getCategoryDefinitions(Object.values(courseDocument.courses));
    elements.categoryManagerEmpty.hidden = categories.length > 0;
    elements.categoryManagerList.replaceChildren(
        ...categories.map((category, index) => {
            const row = document.createElement("div");
            row.className = "category-manager-row";
            row.dataset.categoryName = category.name;
            row.innerHTML = `
                <span class="category-manager-order">${String(index + 1).padStart(2, "0")}</span>
                <input type="text" maxlength="60" aria-label="Rename ${escapeAttribute(category.name)}" />
                <span class="category-manager-count">${category.count} course${category.count === 1 ? "" : "s"}</span>
                <div class="category-manager-actions">
                    <button type="button" data-category-action="up" aria-label="Move ${escapeAttribute(category.name)} up" ${index === 0 ? "disabled" : ""}>↑</button>
                    <button type="button" data-category-action="down" aria-label="Move ${escapeAttribute(category.name)} down" ${index === categories.length - 1 ? "disabled" : ""}>↓</button>
                    <button type="button" data-category-action="rename">Save</button>
                    <button class="is-danger" type="button" data-category-action="delete">Remove</button>
                </div>`;
            row.querySelector<HTMLInputElement>("input")!.value = category.name;
            return row;
        })
    );
}

async function handleCategoryManagerAction(event: Event): Promise<void> {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
        "[data-category-action]"
    );
    const row = button?.closest<HTMLElement>("[data-category-name]");
    if (!button || !row) return;
    const courseDocument = await getLocalDocument();
    const categories = getCategoryDefinitions(Object.values(courseDocument.courses));
    const categoryName = row.dataset.categoryName || "";
    const categoryIndex = categories.findIndex((category) => category.name === categoryName);
    if (categoryIndex < 0) return;
    const now = Date.now();

    if (button.dataset.categoryAction === "rename") {
        const requestedName = normalizeCategoryName(
            row.querySelector<HTMLInputElement>("input")!.value
        );
        if (!requestedName) {
            showToast("Enter a category name");
            return;
        }
        const existing = categories.find(
            (category) => category.name.toLocaleLowerCase() === requestedName.toLocaleLowerCase()
        );
        const nextName = existing?.name || requestedName;
        const nextOrder = existing?.order ?? categories[categoryIndex].order;
        updateCategoryCourses(courseDocument, categoryName, now, () => ({
            categoryName: nextName,
            categoryOrder: nextOrder,
        }));
        showToast(`Category renamed to ${nextName}`);
    } else if (button.dataset.categoryAction === "delete") {
        updateCategoryCourses(courseDocument, categoryName, now, () => ({
            categoryName: "",
            categoryOrder: 0,
        }));
        showToast(`${categoryName} removed · courses are uncategorized`);
    } else {
        const targetIndex = categoryIndex + (button.dataset.categoryAction === "up" ? -1 : 1);
        if (targetIndex < 0 || targetIndex >= categories.length) return;
        [categories[categoryIndex], categories[targetIndex]] = [
            categories[targetIndex],
            categories[categoryIndex],
        ];
        const orderByName = new Map(categories.map((category, index) => [category.name, index]));
        for (const course of Object.values(courseDocument.courses)) {
            if (!course.categoryName || !orderByName.has(course.categoryName)) continue;
            course.categoryOrder = orderByName.get(course.categoryName);
            course.categoryUpdatedAt = now;
            course.courseUpdatedAt = now;
        }
    }

    courseDocument.updatedAt = now;
    await persistCourseDocument(courseDocument, "categories-managed");
    renderCategoryManager(courseDocument);
}

function updateCategoryCourses(
    courseDocument: CourseDocument,
    categoryName: string,
    now: number,
    getChanges: (course: any) => Record<string, unknown>
): void {
    for (const [courseId, course] of Object.entries(courseDocument.courses)) {
        if (course.categoryName !== categoryName) continue;
        courseDocument.courses[courseId] = Sync.recalculateCourse({
            ...course,
            ...getChanges(course),
            categoryUpdatedAt: now,
            courseUpdatedAt: now,
        });
    }
}

async function persistCourseDocument(
    courseDocument: CourseDocument,
    reason: string,
    shouldRender = true,
    marksPlaylistOrderMutation = false
): Promise<void> {
    if (marksPlaylistOrderMutation) playlistOrderMutationGeneration += 1;
    await writeCourseDocument(courseDocument);
    const storedConfiguration = await getRecord<SyncConfiguration>("configuration");
    const configuration = storedConfiguration || activeConfiguration;
    if (configuration && shouldRender) renderDashboard(courseDocument, configuration);
    if (storedConfiguration) void synchronize(storedConfiguration, reason);
}

function writeCourseDocument(
    courseDocument: CourseDocument,
    expectedPlaylistOrderGeneration?: number
): Promise<boolean> {
    const write = courseDocumentWriteQueue.then(async () => {
        if (
            expectedPlaylistOrderGeneration !== undefined &&
            expectedPlaylistOrderGeneration !== playlistOrderMutationGeneration
        ) {
            return false;
        }
        await setRecord("document", courseDocument);
        return true;
    });
    courseDocumentWriteQueue = write.then(
        () => undefined,
        () => undefined
    );
    return write;
}

async function readStableLocalDocument(): Promise<{
    document: CourseDocument;
    playlistOrderGeneration: number;
}> {
    for (;;) {
        const generationBeforeRead = playlistOrderMutationGeneration;
        await courseDocumentWriteQueue;
        const document = await getLocalDocument();
        const generationAfterRead = playlistOrderMutationGeneration;
        if (generationBeforeRead === generationAfterRead) {
            return { document, playlistOrderGeneration: generationAfterRead };
        }
    }
}

function rememberNativeSession(control: HTMLElement): void {
    const courseId = control.dataset.course || "";
    const videoId = control.dataset.video || "";
    if (!courseId || !videoId) return;
    const startingTimestamp = Math.max(
        0,
        Math.floor(Number(control.dataset.startingTimestamp) || 0)
    );
    const pending: PendingNativeSession = {
        courseId,
        videoId,
        startingTimestamp,
        createdAt: Date.now(),
    };
    currentPending = pending;
    void setRecord("pendingNativeSession", pending).catch(() => {
        showToast("Could not save the return check-in. Please try again.");
    });
}

function buildYouTubeWatchUrl(courseId: string, videoId: string, position: number): string {
    if (!videoId) return buildYouTubePlaylistUrl(courseId);
    const url = new URL("https://www.youtube.com/watch");
    url.searchParams.set("v", videoId);
    if (courseId) url.searchParams.set("list", courseId);
    if (position > 0) url.searchParams.set("t", `${Math.floor(position)}s`);
    return url.toString();
}

function buildYouTubePlaylistUrl(courseId: string): string {
    const url = new URL("https://www.youtube.com/playlist");
    url.searchParams.set("list", courseId);
    return url.toString();
}

async function maybePromptForCheckin(): Promise<void> {
    if (elements.checkinDialog.open) return;
    currentPending = (await getRecord<PendingNativeSession>("pendingNativeSession")) || null;
    if (!currentPending) return;
    const document = await getLocalDocument();
    const course = document.courses[currentPending.courseId];
    if (!course) {
        await deleteRecord("pendingNativeSession");
        currentPending = null;
        return;
    }
    const duration = Math.max(1, Number(course.videoDurations?.[currentPending.videoId]) || 1);
    const title = course.videoMetadata?.[currentPending.videoId]?.title || "this lesson";
    checkinMode = "native";
    elements.checkinTitle.textContent = "Where did you stop?";
    elements.checkinCopy.textContent = `You started “${title}” at ${formatClock(currentPending.startingTimestamp)}.`;
    writeCheckinTimeFields(
        currentPending.startingTimestamp,
        elements.checkinStartMinutes,
        elements.checkinStartSeconds
    );
    elements.checkinSlider.max = String(duration);
    elements.checkinSlider.value = String(Math.min(duration, currentPending.startingTimestamp));
    writeCheckinTimeFields(
        currentPending.startingTimestamp,
        elements.checkinMinutes,
        elements.checkinSeconds
    );
    elements.checkinDuration.textContent = formatClock(duration);
    elements.checkinError.textContent = "";
    elements.checkinSaveButton.textContent = "Save watched range";
    elements.checkinDialog.showModal();
}

async function openProgressCorrection(courseId: string, videoId: string): Promise<void> {
    const document = await getLocalDocument();
    const course = document.courses[courseId];
    if (!course || !videoId) return;
    const duration = Math.max(1, Number(course.videoDurations?.[videoId]) || 1);
    const savedPosition = Math.min(
        duration,
        Math.max(0, Number(course.videoPlaybackPositions?.[videoId]) || 0)
    );
    const title = course.videoMetadata?.[videoId]?.title || "this lesson";
    currentPending = { courseId, videoId, startingTimestamp: 0, createdAt: Date.now() };
    checkinMode = "correction";
    elements.checkinTitle.textContent = "Correct watched progress";
    elements.checkinCopy.textContent = `Confirm the continuous section you actually watched in “${title}”.`;
    writeCheckinTimeFields(0, elements.checkinStartMinutes, elements.checkinStartSeconds);
    elements.checkinSlider.max = String(duration);
    elements.checkinSlider.value = String(savedPosition);
    writeCheckinTimeFields(savedPosition, elements.checkinMinutes, elements.checkinSeconds);
    elements.checkinDuration.textContent = formatClock(duration);
    elements.checkinError.textContent = "";
    elements.checkinSaveButton.textContent = "Add watched range";
    elements.checkinDialog.showModal();
}

async function saveCheckin(event: Event): Promise<void> {
    event.preventDefault();
    if (!currentPending) return;
    const startedAt = parseCheckinTimeFields(
        elements.checkinStartMinutes,
        elements.checkinStartSeconds
    );
    const stoppedAt = parseCheckinTimeFields(elements.checkinMinutes, elements.checkinSeconds);
    if (startedAt === null || stoppedAt === null) {
        elements.checkinError.textContent =
            "Enter minutes and seconds using numbers. Seconds must be between 00 and 59.";
        return;
    }
    const duration = Number(elements.checkinSlider.max);
    if (startedAt > duration || stoppedAt > duration) {
        elements.checkinError.textContent = `The time cannot be later than ${formatClock(duration)}.`;
        return;
    }
    if (stoppedAt < startedAt) {
        elements.checkinError.textContent =
            "The ending timestamp must be after the starting timestamp.";
        return;
    }
    elements.checkinError.textContent = "";
    await applyCheckin(startedAt, stoppedAt, false);
}

async function markCheckinComplete(): Promise<void> {
    if (!currentPending) return;
    const document = await getLocalDocument();
    const duration =
        Number(
            document.courses[currentPending.courseId]?.videoDurations?.[currentPending.videoId]
        ) || 0;
    await applyCheckin(0, duration, true);
}

async function applyCheckin(
    startedAt: number,
    stoppedAt: number,
    markComplete: boolean
): Promise<void> {
    if (!currentPending) return;
    const document = await getLocalDocument();
    const course = document.courses[currentPending.courseId];
    if (!course) return;
    const videoId = currentPending.videoId;
    const duration = Math.max(0, Number(course.videoDurations?.[videoId]) || stoppedAt);
    const start = Math.min(duration || startedAt, Math.max(0, Math.floor(startedAt)));
    const stop = Math.min(duration || stoppedAt, Math.max(0, Math.floor(stoppedAt)));
    const now = Date.now();
    const mode = checkinMode;
    document.courses[currentPending.courseId] = Sync.applyConfirmedWatchedRange(
        course,
        videoId,
        start,
        stop,
        { markComplete, updatedAt: now }
    );
    document.updatedAt = now;
    await setRecord("document", document);
    if (mode === "native") await deleteRecord("pendingNativeSession");
    currentPending = null;
    checkinMode = null;
    elements.checkinDialog.close();
    const storedConfiguration = await getRecord<SyncConfiguration>("configuration");
    const configuration = storedConfiguration || activeConfiguration;
    if (configuration) {
        renderDashboard(document, configuration);
        if (storedConfiguration)
            void synchronize(
                storedConfiguration,
                mode === "correction" ? "progress-correction" : "manual-checkin"
            );
    }
    showToast(
        markComplete
            ? "Lesson marked complete"
            : mode === "correction"
              ? "Watched range corrected"
              : "Progress saved"
    );
}

async function discardCheckin(): Promise<void> {
    if (checkinMode === "native") await deleteRecord("pendingNativeSession");
    currentPending = null;
    checkinMode = null;
    elements.checkinDialog.close();
    showToast("No changes made");
}

function openAddCourse(): void {
    elements.addCourseError.textContent = "";
    elements.addCourseForm.reset();
    elements.addCourseDialog.showModal();
}

async function addCourse(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    const configuration = await getRecord<SyncConfiguration>("configuration");
    if (!configuration) return;
    const playlistId = parsePlaylistId(elements.playlistInput.value);
    if (!playlistId) {
        elements.addCourseError.textContent = "Enter a valid YouTube playlist URL or ID.";
        return;
    }
    elements.submitCourseButton.disabled = true;
    elements.submitCourseButton.textContent = "Loading lessons…";
    elements.addCourseError.textContent = "";
    try {
        const response = await fetch(`${configuration.serviceUrl}/api/youtube/playlist`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ playlistId }),
        });
        if (!response.ok) throw new Error(await readApiError(response));
        const payload = (await response.json()) as { course: any };
        const document = await getLocalDocument();
        document.courses[playlistId] = Sync.recalculateCourse(payload.course);
        delete document.tombstones[playlistId];
        document.updatedAt = Date.now();
        await setRecord("document", document);
        elements.addCourseDialog.close();
        renderDashboard(document, configuration);
        void synchronize(configuration, "playlist-added");
        showToast("Playlist added");
    } catch (error) {
        elements.addCourseError.textContent =
            error instanceof Error ? error.message : "Playlist could not be added";
    } finally {
        elements.submitCourseButton.disabled = false;
        elements.submitCourseButton.textContent = "Add playlist";
    }
}

async function startScanner(): Promise<void> {
    elements.scannerError.textContent = "";
    elements.scannerDialog.showModal();
    try {
        cameraStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: "environment" } },
            audio: false,
        });
        elements.scannerVideo.srcObject = cameraStream;
        await elements.scannerVideo.play();
        scanCameraFrame();
    } catch {
        elements.scannerError.textContent =
            "Camera access was declined. Close this sheet and use the one-time typed code instead.";
    }
}

function scanCameraFrame(): void {
    if (!cameraStream || !elements.scannerDialog.open) return;
    if (elements.scannerVideo.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        const canvas = elements.scannerCanvas;
        const width = elements.scannerVideo.videoWidth;
        const height = elements.scannerVideo.videoHeight;
        if (width && height) {
            canvas.width = width;
            canvas.height = height;
            const context = canvas.getContext("2d", { willReadFrequently: true });
            context?.drawImage(elements.scannerVideo, 0, 0, width, height);
            const pixels = context?.getImageData(0, 0, width, height);
            const result = pixels
                ? jsQR(pixels.data, width, height, { inversionAttempts: "dontInvert" })
                : null;
            if (result?.data) {
                void pairFromQr(result.data);
                return;
            }
        }
    }
    scannerFrame = requestAnimationFrame(scanCameraFrame);
}

function stopScanner(): void {
    cancelAnimationFrame(scannerFrame);
    cameraStream?.getTracks().forEach((track) => track.stop());
    cameraStream = null;
    elements.scannerVideo.srcObject = null;
}

async function pairFromQr(payload: string): Promise<void> {
    try {
        const invitation = Sync.decodePairingInvitation(payload) as {
            serviceUrl: string;
            code: string;
        };
        stopScanner();
        elements.scannerError.textContent = "Joining the one-time encrypted exchange…";
        const configuration = await exchangePairingInvitation(
            invitation.serviceUrl,
            invitation.code
        );
        elements.scannerDialog.close();
        await finishPairing({ ...configuration, pairedAt: Date.now() });
    } catch (error) {
        elements.scannerError.textContent =
            error instanceof Error ? error.message : "This is not a Course Desk pairing code";
    }
}

async function handleTypedPairing(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    elements.typedError.textContent = "";
    const submit = elements.typedForm.querySelector<HTMLButtonElement>("button[type=submit]")!;
    submit.disabled = true;
    submit.textContent = "Joining encrypted exchange…";
    try {
        const serviceUrl = normalizeServiceUrl(elements.typedServiceUrl.value);
        const code = elements.typedCode.value.trim().toUpperCase();
        if (!/^[A-Z0-9]{8}$/.test(code)) throw new Error("Enter the full eight-character code.");
        const configuration = await exchangePairingInvitation(serviceUrl, code, () => {
            submit.textContent = "Waiting for Brave…";
        });
        elements.typedDialog.close();
        await finishPairing({ ...configuration, pairedAt: Date.now() });
    } catch (error) {
        elements.typedError.textContent = error instanceof Error ? error.message : "Pairing failed";
    } finally {
        submit.disabled = false;
        submit.textContent = "Pair securely";
    }
}

async function exchangePairingInvitation(
    rawServiceUrl: string,
    rawCode: string,
    onJoined?: () => void
): Promise<SyncConfiguration> {
    const serviceUrl = normalizeServiceUrl(rawServiceUrl);
    const code = rawCode.trim().toUpperCase();
    if (!/^[A-Z0-9]{8}$/.test(code)) throw new Error("The one-time pairing invitation is invalid.");
    const pairingHeaders = { "X-Pairing-Code": code };
    const sessionResponse = await fetch(`${serviceUrl}/api/pair/session`, {
        headers: pairingHeaders,
        cache: "no-store",
    });
    if (!sessionResponse.ok) throw new Error(await readApiError(sessionResponse));
    const session = (await sessionResponse.json()) as { desktopPublicKey: string };
    const desktopPublicKey = await crypto.subtle.importKey(
        "raw",
        Sync.base64UrlToBytes(session.desktopPublicKey),
        { name: "ECDH", namedCurve: "P-256" },
        false,
        []
    );
    const phoneKeyPair = await crypto.subtle.generateKey(
        { name: "ECDH", namedCurve: "P-256" },
        true,
        ["deriveKey"]
    );
    const phonePublicKey = Sync.bytesToBase64Url(
        new Uint8Array(await crypto.subtle.exportKey("raw", phoneKeyPair.publicKey))
    );
    const phoneToken = Sync.randomBase64Url(24);
    const phoneTokenHash = await Sync.sha256Base64Url(phoneToken);
    const joinResponse = await fetch(`${serviceUrl}/api/pair/session/phone`, {
        method: "POST",
        headers: { ...pairingHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ phonePublicKey, phoneTokenHash }),
    });
    if (!joinResponse.ok) throw new Error(await readApiError(joinResponse));
    onJoined?.();
    const exchangeKey = await crypto.subtle.deriveKey(
        { name: "ECDH", public: desktopPublicKey },
        phoneKeyPair.privateKey,
        { name: "AES-GCM", length: 256 },
        false,
        ["decrypt"]
    );
    let envelope: { iv: string; data: string } | null = null;
    for (let attempt = 0; attempt < 360; attempt++) {
        const poll = await fetch(`${serviceUrl}/api/pair/session/bundle`, {
            headers: {
                ...pairingHeaders,
                Authorization: `Bearer ${phoneToken}`,
            },
            cache: "no-store",
        });
        if (poll.ok) {
            const status = (await poll.json()) as {
                ready: boolean;
                envelope?: { iv: string; data: string };
            };
            if (status.ready && status.envelope) {
                envelope = status.envelope;
                break;
            }
        } else if (poll.status !== 202) {
            throw new Error(await readApiError(poll));
        }
        await delay(1500);
    }
    if (!envelope) throw new Error("Pairing timed out. Create a fresh code in Brave.");
    const decrypted = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: Sync.base64UrlToBytes(envelope.iv) },
        exchangeKey,
        Sync.base64UrlToBytes(envelope.data)
    );
    const configuration = JSON.parse(new TextDecoder().decode(decrypted)) as SyncConfiguration;
    if (
        configuration.serviceUrl !== serviceUrl ||
        !configuration.syncId ||
        !configuration.rootSecret
    ) {
        throw new Error("Pairing response did not match this Course Desk.");
    }
    return configuration;
}

async function finishPairing(configuration: SyncConfiguration): Promise<void> {
    await setRecord("configuration", configuration);
    updateSyncChip("syncing");
    await synchronize(configuration, "paired");
    await showDashboard(configuration);
    showToast("Brave and iPhone are paired privately");
}

function synchronize(configuration: SyncConfiguration, reason: string): Promise<void> {
    queuedSynchronizationRequest = { configuration, reason };
    if (synchronizationPromise) return synchronizationPromise;
    const synchronization = (async () => {
        while (queuedSynchronizationRequest) {
            const request: SynchronizationRequest = queuedSynchronizationRequest;
            queuedSynchronizationRequest = null;
            const generationAtStart = playlistOrderMutationGeneration;
            const needsRetry = await performSynchronization(request.configuration, request.reason);
            if (
                (needsRetry || generationAtStart !== playlistOrderMutationGeneration) &&
                !queuedSynchronizationRequest
            ) {
                queuedSynchronizationRequest = {
                    configuration: activeConfiguration || request.configuration,
                    reason: "newer-local-order",
                };
            }
        }
    })().finally(() => {
        if (synchronizationPromise === synchronization) synchronizationPromise = null;
    });
    synchronizationPromise = synchronization;
    return synchronization;
}

async function performSynchronization(
    configuration: SyncConfiguration,
    reason: string
): Promise<boolean> {
    if (!navigator.onLine) {
        updateSyncChip("offline");
        return false;
    }
    if (reason !== "foreground-poll") updateSyncChip("syncing");
    try {
        const { encryptionKey, authenticationToken } = await Sync.deriveSyncCredentials(
            configuration.rootSecret
        );
        for (let attempt = 0; attempt < 3; attempt++) {
            const response = await fetch(`${configuration.serviceUrl}/api/sync/space`, {
                headers: {
                    Authorization: `Bearer ${authenticationToken}`,
                    "X-Sync-Id": configuration.syncId,
                },
                cache: "no-store",
            });
            if (!response.ok) throw new Error(await readApiError(response));
            const remote = (await response.json()) as {
                snapshot: { iv: string; data: string };
                revision: number;
            };
            const remoteDocument = await Sync.decryptDocument(remote.snapshot, encryptionKey);
            const stableLocal = await readStableLocalDocument();
            const localDocument = stableLocal.document;
            const observedPlaylistOrderGeneration = stableLocal.playlistOrderGeneration;
            const merged = Sync.mergeDocuments(remoteDocument, localDocument) as CourseDocument;
            const localChanged = !Sync.documentsEqual(localDocument, merged);
            const remoteChanged = !Sync.documentsEqual(remoteDocument, merged);

            if (!remoteChanged) {
                const wroteMergedDocument = localChanged
                    ? await writeCourseDocument(merged, observedPlaylistOrderGeneration)
                    : true;
                if (!wroteMergedDocument) return true;
                if (observedPlaylistOrderGeneration !== playlistOrderMutationGeneration)
                    return true;
                const now = Date.now();
                const next = { ...configuration, revision: remote.revision, lastSyncedAt: now };
                activeConfiguration = next;
                if (
                    Number(configuration.revision) !== remote.revision ||
                    now - Number(configuration.lastSyncedAt || 0) >=
                        CONFIGURATION_PERSIST_INTERVAL_MS
                ) {
                    await setRecord("configuration", next);
                }
                if (
                    !finishSynchronizationUiIfCurrent(
                        observedPlaylistOrderGeneration,
                        merged,
                        next,
                        localChanged
                    )
                )
                    return true;
                return false;
            }

            if (observedPlaylistOrderGeneration !== playlistOrderMutationGeneration) return true;
            const snapshot = await Sync.encryptDocument(merged, encryptionKey);
            if (observedPlaylistOrderGeneration !== playlistOrderMutationGeneration) return true;
            const save = await fetch(`${configuration.serviceUrl}/api/sync/space`, {
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${authenticationToken}`,
                    "Content-Type": "application/json",
                    "If-Match": String(remote.revision),
                    "X-Sync-Id": configuration.syncId,
                },
                body: JSON.stringify({ snapshot, schemaVersion: Sync.SCHEMA_VERSION }),
            });
            if (save.status === 409) continue;
            if (!save.ok) throw new Error(await readApiError(save));
            const saved = (await save.json()) as { revision: number };
            if (observedPlaylistOrderGeneration !== playlistOrderMutationGeneration) return true;
            const wroteMergedDocument = localChanged
                ? await writeCourseDocument(merged, observedPlaylistOrderGeneration)
                : true;
            if (!wroteMergedDocument) return true;
            if (observedPlaylistOrderGeneration !== playlistOrderMutationGeneration) return true;
            const next = { ...configuration, revision: saved.revision, lastSyncedAt: Date.now() };
            await setRecord("configuration", next);
            activeConfiguration = next;
            if (
                !finishSynchronizationUiIfCurrent(
                    observedPlaylistOrderGeneration,
                    merged,
                    next,
                    localChanged
                )
            )
                return true;
            return false;
        }
        throw new Error("Progress changed on another device. Sync again.");
    } catch (error) {
        console.warn(`Course Desk sync failed (${reason})`, error);
        updateSyncChip("error");
        return false;
    }
}

function finishSynchronizationUiIfCurrent(
    observedPlaylistOrderGeneration: number,
    merged: CourseDocument,
    configuration: SyncConfiguration,
    localChanged: boolean
): boolean {
    if (observedPlaylistOrderGeneration !== playlistOrderMutationGeneration) return false;
    updateSyncChip("synced");
    if (localChanged && !elements.dashboard.hidden) {
        if (observedPlaylistOrderGeneration !== playlistOrderMutationGeneration) return false;
        renderDashboard(merged, configuration);
    }
    return true;
}

function updateSyncChip(state: "ready" | "syncing" | "synced" | "offline" | "error"): void {
    elements.syncChip.hidden = false;
    elements.syncChip.className = `sync-chip is-${state}`;
    const copy = {
        ready: "Ready",
        syncing: "Syncing",
        synced: "Synced",
        offline: "Offline",
        error: "Sync waiting",
    }[state];
    elements.syncChip.querySelector<HTMLElement>("span")!.textContent = copy;
}

async function startAutomaticTracking(courseId: string, videoId: string): Promise<void> {
    const configuration = await getRecord<SyncConfiguration>("configuration");
    if (!configuration) return;
    const document = await getLocalDocument();
    const course = document.courses[courseId];
    if (!course) return;
    elements.automaticTitle.textContent =
        course.videoMetadata?.[videoId]?.title || "Track automatically";
    elements.automaticDialog.showModal();
    automaticSession = {
        document,
        configuration,
        courseId,
        videoId,
        lastPlayerTime: null,
        lastPersistedAt: Date.now(),
    };
    elements.automaticFrame.addEventListener(
        "load",
        () => {
            elements.automaticFrame.contentWindow?.postMessage(
                {
                    type: "course-desk-player-start",
                    videoId,
                    playlistId: courseId,
                    startAt: Math.floor(Number(course.videoPlaybackPositions?.[videoId]) || 0),
                },
                "*"
            );
        },
        { once: true }
    );
    elements.automaticFrame.src = `/player.html?session=${encodeURIComponent(crypto.randomUUID())}`;
}

function handleAutomaticPlayerMessage(event: MessageEvent<unknown>): void {
    if (event.source !== elements.automaticFrame.contentWindow || !isRecord(event.data)) return;
    if (event.data.type === "course-desk-player-error") {
        showToast(String(event.data.message || "YouTube player could not load"));
        return;
    }
    if (event.data.type !== "course-desk-player-progress") return;
    trackPlayerRange(Number(event.data.current), Number(event.data.duration));
    if (Number(event.data.state) !== 1 && automaticSession) {
        automaticSession.lastPlayerTime = null;
        void persistAutomaticSession(true);
    }
}

function trackPlayerRange(rawCurrent: number, rawDuration: number): void {
    const session = automaticSession;
    if (!session || document.hidden) return;
    const course = session.document.courses[session.courseId];
    const current = Math.max(0, Number(rawCurrent) || 0);
    const duration = Math.max(
        0,
        Number(rawDuration) || Number(course.videoDurations?.[session.videoId]) || 0
    );
    const previous = session.lastPlayerTime;
    if (previous !== null && current > previous && current - previous <= 2.5) {
        course.videoWatchedRanges ||= {};
        course.videoWatchedRanges[session.videoId] = Sync.unionRanges(
            course.videoWatchedRanges[session.videoId],
            [[previous, current]],
            duration || Infinity
        );
    }
    const now = Date.now();
    course.videoDurations[session.videoId] = duration;
    course.videoPlaybackPositions[session.videoId] = Math.floor(current);
    course.videoPositionUpdatedAt ||= {};
    course.videoPositionUpdatedAt[session.videoId] = now;
    course.lastWatchedVideoId = session.videoId;
    course.lastInteractedAt = now;
    course.courseUpdatedAt = now;
    session.document.courses[session.courseId] = Sync.recalculateCourse(course);
    session.document.updatedAt = now;
    session.lastPlayerTime = current;
    if (now - session.lastPersistedAt >= 5000) void persistAutomaticSession(false);
}

async function persistAutomaticSession(forceSync: boolean): Promise<void> {
    const session = automaticSession;
    if (!session) return;
    await setRecord("document", session.document);
    session.lastPersistedAt = Date.now();
    renderDashboard(session.document, session.configuration);
    if (forceSync) void synchronize(session.configuration, "automatic-player");
}

function stopAutomaticTracking(): void {
    if (!automaticSession) return;
    elements.automaticFrame.contentWindow?.postMessage({ type: "course-desk-player-destroy" }, "*");
    void persistAutomaticSession(true);
    automaticSession = null;
    elements.automaticFrame.src = "about:blank";
}

async function getLocalDocument(): Promise<CourseDocument> {
    return (
        (await getRecord<CourseDocument>("document")) || {
            schemaVersion: 2,
            courses: {},
            tombstones: {},
            updatedAt: 0,
        }
    );
}

function makePreviewDocument(): CourseDocument {
    const now = Date.now();
    const durations = { lesson1: 3998, lesson2: 2456, lesson3: 2779 };
    const course = Sync.recalculateCourse({
        schemaVersion: 2,
        courseName: "الدروس المهمة لعامة الأمة – الشيخ سالم الطويل",
        courseImgSrc: "/icon.svg",
        totalDuration: { hours: 2, minutes: 33, seconds: 53 },
        watchedDuration: { hours: 0, minutes: 0, seconds: 0 },
        investedTime: { hours: 0, minutes: 0, seconds: 0 },
        videoWatchStatus: { lesson1: false, lesson2: false, lesson3: false },
        videoPlaybackPositions: { lesson1: 254, lesson2: 0, lesson3: 0 },
        videoWatchedSeconds: { lesson1: 254, lesson2: 0, lesson3: 0 },
        videoWatchedRanges: { lesson1: [[0, 254]], lesson2: [], lesson3: [] },
        videoDurations: durations,
        videoMetadata: {
            lesson1: { title: "الدرس (١) شرح رسالة الشيخ ابن باز", thumbnailUrl: "", index: 0 },
            lesson2: { title: "الدرس (٢) الدروس المهمة", thumbnailUrl: "", index: 1 },
            lesson3: { title: "الدرس (٣) شرح العقيدة", thumbnailUrl: "", index: 2 },
        },
        videoPositionUpdatedAt: { lesson1: now },
        videoStatusUpdatedAt: {},
        categoryName: "Arabic studies",
        categoryOrder: 0,
        categoryUpdatedAt: now,
        lastWatchedVideoId: "lesson1",
        lastInteractedAt: now,
        courseUpdatedAt: now,
    });
    return {
        schemaVersion: 2,
        courses: { PL_LOCAL_PREVIEW: course },
        tombstones: {},
        updatedAt: now,
    };
}

function parsePlaylistId(value: string): string | null {
    const input = value.trim();
    if (/^[A-Za-z0-9_-]{10,}$/.test(input)) return input;
    try {
        const url = new URL(input);
        if (
            !(
                url.hostname === "youtu.be" ||
                url.hostname === "youtube.com" ||
                url.hostname.endsWith(".youtube.com")
            )
        )
            return null;
        const id = url.searchParams.get("list") || "";
        return /^[A-Za-z0-9_-]{10,}$/.test(id) ? id : null;
    } catch {
        return null;
    }
}

function normalizeServiceUrl(value: string): string {
    const url = new URL(value.trim());
    if (url.protocol !== "https:" && !["localhost", "127.0.0.1"].includes(url.hostname))
        throw new Error("Use the secure HTTPS Course Desk address.");
    return url.origin;
}

function formatClock(value: number): string {
    const seconds = Math.max(0, Math.floor(Number(value) || 0));
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainder = seconds % 60;
    return hours
        ? `${hours}:${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`
        : `${minutes}:${String(remainder).padStart(2, "0")}`;
}

function parseCheckinTimeFields(
    minutesInput: Pick<HTMLInputElement, "value">,
    secondsInput: Pick<HTMLInputElement, "value">
): number | null {
    const minutesText = normalizeDecimalDigits(minutesInput.value).trim();
    const secondsText = normalizeDecimalDigits(secondsInput.value).trim();
    if (!minutesText && !secondsText) return null;
    if ((minutesText && !/^\d+$/.test(minutesText)) || (secondsText && !/^\d+$/.test(secondsText)))
        return null;
    const minutes = Number(minutesText || 0);
    const seconds = Number(secondsText || 0);
    if (!Number.isSafeInteger(minutes) || !Number.isSafeInteger(seconds) || seconds >= 60)
        return null;
    const total = minutes * 60 + seconds;
    return Number.isSafeInteger(total) ? total : null;
}

function writeCheckinTimeFields(
    value: number,
    minutesInput: Pick<HTMLInputElement, "value">,
    secondsInput: Pick<HTMLInputElement, "value">
): void {
    const total = Math.max(0, Math.floor(Number(value) || 0));
    minutesInput.value = String(Math.floor(total / 60));
    secondsInput.value = String(total % 60).padStart(2, "0");
}

function normalizeDecimalDigits(value: string): string {
    const arabicIndic = "٠١٢٣٤٥٦٧٨٩";
    const easternArabic = "۰۱۲۳۴۵۶۷۸۹";
    return value.replace(/[٠-٩۰-۹]/g, (digit) => {
        const arabicIndex = arabicIndic.indexOf(digit);
        return String(arabicIndex >= 0 ? arabicIndex : easternArabic.indexOf(digit));
    });
}

function formatDuration(value: number): string {
    const seconds = Math.max(0, Math.round(Number(value) || 0));
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours) return `${hours}h ${minutes}m`;
    if (minutes) return `${minutes}m`;
    return `${seconds}s`;
}

function escapeAttribute(value: string): string {
    return String(value).replace(
        /[&"<>]/g,
        (character) =>
            ({ "&": "&amp;", '"': "&quot;", "<": "&lt;", ">": "&gt;" })[character] || character
    );
}

function showToast(message: string): void {
    elements.toast.textContent = message;
    elements.toast.classList.add("is-visible");
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => elements.toast.classList.remove("is-visible"), 2600);
}

async function unpairThisDevice(): Promise<void> {
    if (
        !window.confirm(
            "Remove Course Desk data and pairing keys from this phone? Brave and the encrypted cloud copy will remain."
        )
    )
        return;
    await Promise.all([
        deleteRecord("configuration"),
        deleteRecord("document"),
        deleteRecord("pendingNativeSession"),
    ]);
    window.location.reload();
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value) && typeof value === "object";
}

async function readApiError(response: Response): Promise<string> {
    try {
        const payload = (await response.json()) as { error?: string };
        return payload.error || `Request failed (${response.status})`;
    } catch {
        return `Request failed (${response.status})`;
    }
}

function delay(milliseconds: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function byId<T extends HTMLElement>(id: string): T {
    const element = document.getElementById(id);
    if (!element) throw new Error(`Missing interface element: ${id}`);
    return element as T;
}

async function openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => request.result.createObjectStore(STORE_NAME);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

async function getRecord<T>(key: string): Promise<T | undefined> {
    const database = await openDatabase();
    return new Promise((resolve, reject) => {
        const request = database.transaction(STORE_NAME).objectStore(STORE_NAME).get(key);
        request.onsuccess = () => {
            database.close();
            resolve(request.result as T | undefined);
        };
        request.onerror = () => {
            database.close();
            reject(request.error);
        };
    });
}

async function setRecord(key: string, value: unknown): Promise<void> {
    const database = await openDatabase();
    return new Promise((resolve, reject) => {
        const transaction = database.transaction(STORE_NAME, "readwrite");
        transaction.objectStore(STORE_NAME).put(value, key);
        transaction.oncomplete = () => {
            database.close();
            resolve();
        };
        transaction.onerror = () => {
            database.close();
            reject(transaction.error);
        };
    });
}

async function deleteRecord(key: string): Promise<void> {
    const database = await openDatabase();
    return new Promise((resolve, reject) => {
        const transaction = database.transaction(STORE_NAME, "readwrite");
        transaction.objectStore(STORE_NAME).delete(key);
        transaction.oncomplete = () => {
            database.close();
            resolve();
        };
        transaction.onerror = () => {
            database.close();
            reject(transaction.error);
        };
    });
}
